import React, { useState, useEffect, useMemo } from 'react';
import { API_ENDPOINTS, getAuthHeaders } from '../../config/api';
import { safeRemoveNode } from '../../utils/safeDom';
import './CabinetModal.css';

// Configuration des types de structure et leurs IDCC par défaut
const structureTypes = [
  { value: 'liberal', label: 'Cabinet libéral', defaultIdcc: '1619', description: 'Cabinet dentaire libéral' },
  { value: 'dentaire', label: 'Centre dentaire', defaultIdcc: '1619', description: 'Centre dentaire' },
  { value: 'pluridisciplinaire', label: 'Centre pluridisciplinaire', defaultIdcc: '', description: 'Centre pluridisciplinaire' }
];


// --- Shortlists d'organismes ---
const ORG_SHORTLIST = {
  prevoyance: [
    { value: "apgis", label: "APGIS" },          // CCN 1619 obligatoire
    { value: "ag2r", label: "AG2R La Mondiale" },
    { value: "malakoff", label: "Malakoff Humanis" },
    { value: "klesia", label: "KLESIA" },
    { value: "mutex", label: "Mutex" },
    { value: "generali", label: "Generali" },
    { value: "axa", label: "AXA" },
    { value: "allianz", label: "Allianz" },
    { value: "harmonie", label: "Harmonie Mutuelle" },
    { value: "autre", label: "Autre…" },
  ],
  mutuelle: [
    { value: "apgis", label: "APGIS" },          // CCN 1619 obligatoire
    { value: "harmonie", label: "Harmonie Mutuelle" },
    { value: "aesio", label: "AÉSIO Mutuelle" },
    { value: "malakoff", label: "Malakoff Humanis" },
    { value: "ag2r", label: "AG2R La Mondiale" },
    { value: "mutex", label: "Mutex" },
    { value: "generali", label: "Generali" },
    { value: "axa", label: "AXA" },
    { value: "allianz", label: "Allianz" },
    { value: "autre", label: "Autre…" },
  ],
  retraite_comp: [
    { value: "malakoff", label: "Malakoff Humanis (Agirc-Arrco)" },
    { value: "ag2r", label: "AG2R La Mondiale (Agirc-Arrco)" },
    { value: "klesia", label: "KLESIA (Agirc-Arrco)" },
    { value: "apicil", label: "Apicil (Agirc-Arrco)" },
    { value: "audiens", label: "Audiens (Agirc-Arrco)" },
    { value: "b2v", label: "B2V (Agirc-Arrco)" },
    { value: "irpauto", label: "IRP Auto (Agirc-Arrco)" },
    { value: "probtp", label: "Pro BTP (Agirc-Arrco)" },
    { value: "humanis", label: "Humanis (Agirc-Arrco)" },
    { value: "autre", label: "Autre…" },
  ],
};

/**
 * Modal pour créer/mettre à jour les informations employeur (cabinets)
 * Utilisé pour la génération des fiches de paie
 */
export function CabinetModal({
  open,
  onOpenChange,
  existing = null,
  onSaved
}) {
  console.log('🔧 CabinetModal component loaded - VERSION 2.0');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({});

  // Valeurs par défaut du formulaire
  const defaultValues = useMemo(() => ({
    raison_sociale: existing?.raison_sociale || '',
    type_structure: existing?.type_structure || 'liberal',
    convention_idcc: existing?.convention_idcc || '1619',
    siret: existing?.siret || '',
    naf: existing?.naf || '',
    taux_at_mp: existing?.taux_at_mp || '1.10', // Valeur par défaut pour cabinets dentaires
    adresse_l1: existing?.adresse_l1 || '',
    adresse_l2: existing?.adresse_l2 || '',
    code_postal: existing?.code_postal || '',
    ville: existing?.ville || '',
    pays: existing?.pays || 'FR',
    organisme_prevoyance: existing?.organismes?.prevoyance || '',
    organisme_mutuelle: existing?.organismes?.mutuelle || '',
    organisme_retraite_comp: existing?.organismes?.retraite_comp || '',
    note: existing?.meta?.note || existing?.note || '',
    iban: existing?.iban || '',
    bic: existing?.bic || '',
    // Nouveaux champs URSSAF
    urssaf_compte: existing?.urssaf_compte || '',
    // Complémentaires - Mutuelle
    mutuelle_name: existing?.mutuelle_name || existing?.organismes?.mutuelle || '',
    mutuelle_premium_total: existing?.mutuelle_premium_total ?? '',
    mutuelle_employer_share_percent: existing?.mutuelle_employer_share_percent ?? '',
    // Complémentaires - Prévoyance
    prevoyance_name: existing?.prevoyance_name || existing?.organismes?.prevoyance || '',
    prevoyance_mode: existing?.prevoyance_mode || 'FORFAIT',
    prevoyance_premium_total: existing?.prevoyance_premium_total ?? '',
    prevoyance_employer_share_percent: existing?.prevoyance_employer_share_percent ?? '',
    prevoyance_t1_rate_employee: existing?.prevoyance_t1_rate_employee ?? '',
    prevoyance_t1_rate_employer: existing?.prevoyance_t1_rate_employer ?? '',
    prevoyance_t2_rate_employee: existing?.prevoyance_t2_rate_employee ?? '',
    prevoyance_t2_rate_employer: existing?.prevoyance_t2_rate_employer ?? ''
  }), [existing]);

  // Réinitialiser le formulaire à l'ouverture
  useEffect(() => {
    if (open) {
      setFormData(defaultValues);
      setErrors({});
    }
  }, [open, defaultValues]);

  // Gestion des changements de champ simple
  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Si changement de type de structure, mettre à jour l'IDCC par défaut
      if (field === 'type_structure') {
        const structureType = structureTypes.find(st => st.value === value);
        if (structureType && structureType.defaultIdcc) {
          newData.convention_idcc = structureType.defaultIdcc;
        }
      }
      
      // Auto-remplissage : si organisme_mutuelle change et mutuelle_name est vide, copier le label
      if (field === 'organisme_mutuelle' && value && !prev.mutuelle_name) {
        const option = ORG_SHORTLIST.mutuelle.find(org => org.value === value);
        if (option && option.value !== 'autre') {
          newData.mutuelle_name = option.label;
        }
      }
      
      // Auto-remplissage : si organisme_prevoyance change et prevoyance_name est vide, copier le label
      if (field === 'organisme_prevoyance' && value && !prev.prevoyance_name) {
        const option = ORG_SHORTLIST.prevoyance.find(org => org.value === value);
        if (option && option.value !== 'autre') {
          newData.prevoyance_name = option.label;
        }
      }
      
      // Si changement de prevoyance_mode, réinitialiser les champs de l'autre mode
      if (field === 'prevoyance_mode') {
        if (value === 'FORFAIT') {
          // Passer en FORFAIT : réinitialiser les champs PERCENT
          newData.prevoyance_t1_rate_employee = '';
          newData.prevoyance_t1_rate_employer = '';
          newData.prevoyance_t2_rate_employee = '';
          newData.prevoyance_t2_rate_employer = '';
        } else if (value === 'PERCENT') {
          // Passer en PERCENT : réinitialiser les champs FORFAIT
          newData.prevoyance_premium_total = '';
          newData.prevoyance_employer_share_percent = '';
        }
      }
      
      return newData;
    });
    
    // Effacer l'erreur du champ modifié
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Validation des champs
  const validateField = (field, value) => {
    switch (field) {
      case 'raison_sociale':
        if (!value || value.trim().length < 2) return 'Raison sociale requise (min 2 caractères)';
        if (value.length > 120) return 'Raison sociale trop longue (max 120 caractères)';
        break;
      case 'type_structure':
        if (!value) return 'Type de structure requis';
        break;
      case 'siret':
        if (!value) return 'SIRET requis';
        if (!/^\d{14}$/.test(value)) return 'SIRET invalide (14 chiffres requis)';
        break;
      case 'naf':
        if (!value) return 'Code NAF/APE requis';
        if (!/^\d{4}[A-Z]$/.test(value)) return 'Code NAF/APE invalide (ex: 8623Z)';
        break;
      case 'convention_idcc':
        if (!value) return 'Convention collective requise';
        if (!/^\d{3,5}$/.test(value)) return 'IDCC invalide (3-5 chiffres)';
        break;
      case 'adresse_l1':
        if (!value || value.trim().length < 3) return 'Adresse ligne 1 requise (min 3 caractères)';
        break;
      case 'code_postal':
        if (!value) return 'Code postal requis';
        if (formData.pays === 'FR' && !/^\d{5}$/.test(value)) return 'Code postal français invalide (5 chiffres)';
        if (formData.pays !== 'FR' && !/^[0-9A-Za-z\- ]{4,10}$/.test(value)) return 'Code postal invalide';
        break;
      case 'ville':
        if (!value || value.trim().length < 2) return 'Ville requise (min 2 caractères)';
        break;
      case 'pays':
        if (!value || value.trim().length < 2) return 'Pays requis';
        break;
      case 'iban':
        if (value && !/^[A-Z]{2}[0-9A-Z]{2,30}$/.test(value.replace(/\s/g, ''))) {
          return 'IBAN invalide';
        }
        break;
      case 'bic':
        if (value && !/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value)) {
          return 'BIC invalide';
        }
        break;
      case 'taux_at_mp':
        if (value && (isNaN(value) || value < 0 || value > 100)) {
          return 'Taux AT/MP invalide (0-100%)';
        }
        break;
      case 'urssaf_compte':
        if (value && !/^[0-9A-Za-z\-\/\. ]{3,50}$/.test(value)) {
          return 'Compte URSSAF invalide (3-50 caractères)';
        }
        break;
      case 'mutuelle_premium_total':
      case 'prevoyance_premium_total':
        if (value !== '' && (isNaN(value) || parseFloat(value) < 0)) {
          return 'Montant invalide (≥ 0)';
        }
        break;
      case 'mutuelle_employer_share_percent':
      case 'prevoyance_employer_share_percent':
      case 'prevoyance_t1_rate_employee':
      case 'prevoyance_t1_rate_employer':
      case 'prevoyance_t2_rate_employee':
      case 'prevoyance_t2_rate_employer':
        if (value !== '' && (isNaN(value) || parseFloat(value) < 0 || parseFloat(value) > 100)) {
          return 'Pourcentage invalide (0-100%)';
        }
        break;
      case 'prevoyance_mode':
        if (value && !['FORFAIT', 'PERCENT'].includes(value)) {
          return 'Mode invalide (FORFAIT ou PERCENT)';
        }
        break;
      default:
        break;
    }
    return null;
  };


  // Sélection dynamique par IDCC (APGIS prioritaire pour 1619)
  const getOrgOptions = (idcc) => {
    if (idcc === "1619") {
      return {
        prevoyance: ORG_SHORTLIST.prevoyance,
        mutuelle: ORG_SHORTLIST.mutuelle,
        retraite_comp: ORG_SHORTLIST.retraite_comp,
      };
    }
    // sinon on garde la même shortlist mais sans imposer APGIS en premier (déjà présent)
    return ORG_SHORTLIST;
  };

  // Obtenir les options d'organismes selon la CCN
  const getOrganismeOptions = (type) => {
    const ccn = formData.convention_idcc;
    const orgOptions = getOrgOptions(ccn);
    return orgOptions[type] || [];
  };

  // Vérifier si un organisme est vide
  const isOrganismeEmpty = (type) => {
    return !formData[`organisme_${type}`];
  };

  // Vérifier si au moins un organisme est vide
  const hasEmptyOrganismes = () => {
    return isOrganismeEmpty('prevoyance') || 
           isOrganismeEmpty('mutuelle') || 
           isOrganismeEmpty('retraite_comp');
  };

  // Obtenir le message d'alerte pour un organisme
  const getOrganismeAlertMessage = (type) => {
    const ccn = formData.convention_idcc;
    if (ccn === '1619') {
      return `Recommandé : l'IDCC 1619 impose cet organisme. En l'absence de paramétrage, la paie peut être non conforme.`;
    }
    return `Recommandé pour un calcul complet et des déclarations exactes.`;
  };

  // Vérifier si le taux AT/MP est la valeur par défaut (non confirmée)
  const isAtmpDefault = () => {
    return formData.taux_at_mp === '1.10' || formData.taux_at_mp === 1.10;
  };

  // Validation complète du formulaire
  const validateForm = () => {
    console.log('🔧 Cabinet Modal - Starting form validation');
    const newErrors = {};
    const requiredFields = ['raison_sociale', 'type_structure', 'siret', 'naf', 'convention_idcc', 'taux_at_mp', 'adresse_l1', 'code_postal', 'ville', 'pays'];
    
    requiredFields.forEach(field => {
      const value = formData[field];
      const error = validateField(field, value);
      console.log(`🔧 Cabinet Modal - Field ${field}: value="${value}", error="${error}"`);
      if (error) newErrors[field] = error;
    });

    // Validation des champs optionnels (basiques)
    ['iban', 'bic', 'urssaf_compte'].forEach(field => {
      if (formData[field]) {
        const error = validateField(field, formData[field]);
        if (error) newErrors[field] = error;
      }
    });

    // Validation des complémentaires - Mutuelle (optionnels)
    ['mutuelle_premium_total', 'mutuelle_employer_share_percent'].forEach(field => {
      if (formData[field] !== '') {
        const error = validateField(field, formData[field]);
        if (error) newErrors[field] = error;
      }
    });

    // Validation croisée - Prévoyance selon le mode
    if (formData.prevoyance_mode === 'FORFAIT') {
      // Mode FORFAIT : valider premium_total et employer_share_percent si renseignés
      ['prevoyance_premium_total', 'prevoyance_employer_share_percent'].forEach(field => {
        if (formData[field] !== '') {
          const error = validateField(field, formData[field]);
          if (error) newErrors[field] = error;
        }
      });
    } else if (formData.prevoyance_mode === 'PERCENT') {
      // Mode PERCENT : valider les 4 taux T1/T2 si renseignés
      ['prevoyance_t1_rate_employee', 'prevoyance_t1_rate_employer', 
       'prevoyance_t2_rate_employee', 'prevoyance_t2_rate_employer'].forEach(field => {
        if (formData[field] !== '') {
          const error = validateField(field, formData[field]);
          if (error) newErrors[field] = error;
        }
      });
    }

    console.log('🔧 Cabinet Modal - Validation errors:', newErrors);
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    console.log('🔧 Cabinet Modal - Form is valid:', isValid);
    return isValid;
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔧 Cabinet Modal - handleSubmit called - VERSION 2.0');
    console.log('🔧 FORM SUBMIT EVENT:', e);
    
    const isValid = validateForm();
    console.log('🔧 Cabinet Modal - Validation result:', isValid);
    console.log('🔧 Cabinet Modal - Current formData:', formData);
    console.log('🔧 Cabinet Modal - Current errors:', errors);
    
    if (!isValid) {
      console.log('🔧 Cabinet Modal - Form validation failed, stopping submission');
      return;
    }

    setLoading(true);
    try {
      // Construire les organismes depuis les selects
      const organismes = {
        prevoyance: formData.organisme_prevoyance || undefined,
        mutuelle: formData.organisme_mutuelle || undefined,
        retraite_comp: formData.organisme_retraite_comp || undefined
      };

      // Construire le DSN automatiquement
      const dsn = {
        siret: formData.siret,
        idcc: formData.convention_idcc
      };

      // Préparer les données pour l'API (structure attendue par le backend)
      const payload = {
        raison_sociale: formData.raison_sociale.trim(),
        type_structure: formData.type_structure,
        siret: formData.siret,
        naf: formData.naf.toUpperCase(),
        convention_idcc: formData.convention_idcc,
        taux_at_mp: formData.taux_at_mp ? parseFloat(formData.taux_at_mp) : null,
        adresse: {
          l1: formData.adresse_l1.trim(),
          l2: formData.adresse_l2?.trim() || null,
          code_postal: formData.code_postal,
          ville: formData.ville.trim(),
          pays: formData.pays
        },
        organismes: organismes,
        dsn: dsn,
        meta: {
          note: formData.note?.trim() || undefined
        },
        iban: formData.iban?.replace(/\s/g, '').toUpperCase() || null,
        bic: formData.bic?.toUpperCase() || null,
        // Nouveaux champs URSSAF
        urssaf_compte: formData.urssaf_compte?.trim() || null,
        // Complémentaires - Mutuelle
        mutuelle_name: formData.mutuelle_name?.trim() || null,
        mutuelle_premium_total: formData.mutuelle_premium_total !== '' ? Number(formData.mutuelle_premium_total) : null,
        mutuelle_employer_share_percent: formData.mutuelle_employer_share_percent !== '' ? Number(formData.mutuelle_employer_share_percent) : null,
        // Complémentaires - Prévoyance
        prevoyance_name: formData.prevoyance_name?.trim() || null,
        prevoyance_mode: formData.prevoyance_mode || null
      };

      // Ajouter les champs de prévoyance selon le mode choisi (ne pas envoyer les champs de l'autre mode)
      if (formData.prevoyance_mode === 'FORFAIT') {
        // Mode FORFAIT : envoyer seulement premium_total et employer_share_percent
        if (formData.prevoyance_premium_total !== '') {
          payload.prevoyance_premium_total = Number(formData.prevoyance_premium_total);
        }
        if (formData.prevoyance_employer_share_percent !== '') {
          payload.prevoyance_employer_share_percent = Number(formData.prevoyance_employer_share_percent);
        }
      } else if (formData.prevoyance_mode === 'PERCENT') {
        // Mode PERCENT : envoyer seulement les taux T1/T2
        if (formData.prevoyance_t1_rate_employee !== '') {
          payload.prevoyance_t1_rate_employee = Number(formData.prevoyance_t1_rate_employee);
        }
        if (formData.prevoyance_t1_rate_employer !== '') {
          payload.prevoyance_t1_rate_employer = Number(formData.prevoyance_t1_rate_employer);
        }
        if (formData.prevoyance_t2_rate_employee !== '') {
          payload.prevoyance_t2_rate_employee = Number(formData.prevoyance_t2_rate_employee);
        }
        if (formData.prevoyance_t2_rate_employer !== '') {
          payload.prevoyance_t2_rate_employer = Number(formData.prevoyance_t2_rate_employer);
        }
      }

      const method = existing?.id ? 'PATCH' : 'POST';
      const url = existing?.id 
        ? API_ENDPOINTS.CABINET_DETAIL(existing.id)
        : API_ENDPOINTS.CABINETS_LIST();

      console.log('🔧 Cabinet Modal - Sending request:', {
        method,
        url,
        payload,
        headers: getAuthHeaders()
      });
      
      console.log('🔧 Cabinet Modal - Payload details:');
      console.log('  - raison_sociale:', payload.raison_sociale);
      console.log('  - siret:', payload.siret);
      console.log('  - naf:', payload.naf);
      console.log('  - convention_idcc:', payload.convention_idcc);
      console.log('  - taux_at_mp:', payload.taux_at_mp);
      console.log('  - adresse:', payload.adresse);

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      console.log('🔧 Cabinet Modal - Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('🔧 Cabinet Modal - Error response:', errorData);
        console.error('🔧 Cabinet Modal - Error details:', errorData.error);
        
        // Afficher les détails de validation Joi si disponibles
        if (errorData.error?.details) {
          console.error('🔧 Cabinet Modal - Validation errors:', errorData.error.details);
        }
        
        throw new Error(errorData.error?.message || `Erreur HTTP: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('🔧 Cabinet Modal - Save successful:', responseData);

      // Succès avec message conditionnel
      const ccn = formData.convention_idcc;
      const hasEmpty = hasEmptyOrganismes();
      
      if (ccn === '1619' && hasEmpty) {
        showToast('Cabinet enregistré. Attention : organismes manquants pour la CCN 1619 — paie potentiellement incomplète.', 'info');
      } else {
        showToast('Cabinet enregistré avec succès', 'success');
      }
      
      onOpenChange(false);
      onSaved?.();

    } catch (error) {
      console.error('🔧 Cabinet Modal - Save error:', error);
      showToast(error.message || 'Erreur lors de la sauvegarde', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour afficher les toasts
  const showToast = (message, type = 'success') => {
    // Créer un toast simple
    const toast = document.createElement('div');
    toast.className = `cabinet-toast cabinet-toast-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('show');
    }, 100);

    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => {
        safeRemoveNode(toast);
      }, 300);
    }, 3000);
  };


  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="cabinet-modal-backdrop" onClick={() => onOpenChange(false)} />
      
      {/* Modal */}
      <div className="cabinet-modal">
        <div className="cabinet-modal-content">
          {/* Header */}
          <div className="cabinet-modal-header">
            <h2 className="cabinet-modal-title">Informations employeur</h2>
            <p className="cabinet-modal-subtitle">
              Renseignez ces champs pour activer la génération des fiches de paie
            </p>
            <button 
              className="cabinet-modal-close"
              onClick={() => onOpenChange(false)}
              aria-label="Fermer"
            >
              ×
            </button>
          </div>



          {/* Form */}
          <form onSubmit={handleSubmit} className="cabinet-modal-body">
            {/* Bloc 1 - Type de structure & CCN */}
            <section className="cabinet-section">
              <h3 className="cabinet-section-title">Type de structure & CCN</h3>
              <div className="cabinet-grid">
                {/* Type de structure */}
                <div className="cabinet-field">
                  <label htmlFor="type_structure" className="cabinet-label">
                    Type de structure <span className="required">*</span>
                  </label>
                  <select
                    id="type_structure"
                    className={`cabinet-input cabinet-select ${errors.type_structure ? 'error' : ''}`}
                    value={formData.type_structure || ''}
                    onChange={(e) => handleChange('type_structure', e.target.value)}
                  >
                    <option value="">Sélectionner...</option>
                    {structureTypes.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.type_structure && <span className="error-message">{errors.type_structure}</span>}
                </div>

                {/* Convention IDCC */}
                <div className="cabinet-field">
                  <label htmlFor="convention_idcc" className="cabinet-label">
                    Convention collective (IDCC) <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="convention_idcc"
                    className={`cabinet-input ${errors.convention_idcc ? 'error' : ''}`}
                    value={formData.convention_idcc || ''}
                    onChange={(e) => handleChange('convention_idcc', e.target.value)}
                    placeholder="ex: 1619"
                  />
                  {errors.convention_idcc && <span className="error-message">{errors.convention_idcc}</span>}
                </div>
              </div>
            </section>

            {/* Séparateur */}
            <div className="cabinet-separator" />

            {/* Bloc 2 - Identité */}
            <section className="cabinet-section">
              <h3 className="cabinet-section-title">Identité</h3>
              <div className="cabinet-grid">
                {/* Raison sociale */}
                <div className="cabinet-field">
                  <label htmlFor="raison_sociale" className="cabinet-label">
                    Raison sociale <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="raison_sociale"
                    className={`cabinet-input ${errors.raison_sociale ? 'error' : ''}`}
                    value={formData.raison_sociale || ''}
                    onChange={(e) => handleChange('raison_sociale', e.target.value)}
                    placeholder="Nom de votre cabinet"
                  />
                  {errors.raison_sociale && <span className="error-message">{errors.raison_sociale}</span>}
                </div>

                {/* SIRET */}
                <div className="cabinet-field">
                  <label htmlFor="siret" className="cabinet-label">
                    SIRET <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="siret"
                    className={`cabinet-input ${errors.siret ? 'error' : ''}`}
                    value={formData.siret || ''}
                    onChange={(e) => handleChange('siret', e.target.value)}
                    placeholder="14 chiffres"
                  />
                  {errors.siret && <span className="error-message">{errors.siret}</span>}
                </div>

                {/* Code NAF */}
                <div className="cabinet-field">
                  <label htmlFor="naf" className="cabinet-label">
                    Code NAF/APE <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="naf"
                    className={`cabinet-input ${errors.naf ? 'error' : ''}`}
                    value={formData.naf || ''}
                    onChange={(e) => handleChange('naf', e.target.value)}
                    placeholder="ex: 8623Z"
                  />
                  {errors.naf && <span className="error-message">{errors.naf}</span>}
                </div>

                {/* Taux AT/MP */}
                <div className="cabinet-field">
                  <label htmlFor="taux_at_mp" className="cabinet-label">
                    Taux AT/MP (%) <span className="required">*</span>
                    {isAtmpDefault() && (
                      <span className="atmp-badge-confirm">À confirmer</span>
                    )}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    id="taux_at_mp"
                    className={`cabinet-input ${errors.taux_at_mp ? 'error' : ''}`}
                    value={formData.taux_at_mp || ''}
                    onChange={(e) => handleChange('taux_at_mp', e.target.value)}
                    placeholder="ex: 1.10"
                  />
                  <div className="cabinet-atmp-help">
                    <strong>Taux AT/MP (Accidents du travail / Maladies professionnelles)</strong>
                    <p>
                      Ce taux est fixé chaque année par la <strong>CARSAT</strong> pour votre établissement (SIRET).
                    </p>
                    <ul>
                      <li>Pour les cabinets dentaires, le taux collectif est généralement proche de <strong>1,10 %</strong>.</li>
                      <li>Le taux exact figure sur votre <strong>notification AT/MP</strong> disponible dans votre{' '}
                        <a 
                          href="https://www.net-entreprises.fr" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="help-link"
                        >
                          Compte AT/MP sur Net-Entreprises
                        </a>
                        . Veuillez le vérifier et le mettre à jour.
                      </li>
                    </ul>
                  </div>
                  {errors.taux_at_mp && <span className="error-message">{errors.taux_at_mp}</span>}
                </div>
              </div>
            </section>

            {/* Séparateur */}
            <div className="cabinet-separator" />

            {/* Bloc 3 - Coordonnées */}
            <section className="cabinet-section">
              <h3 className="cabinet-section-title">Coordonnées</h3>
              <div className="cabinet-grid">
                {/* Adresse ligne 1 */}
                <div className="cabinet-field">
                  <label htmlFor="adresse_l1" className="cabinet-label">
                    Adresse (ligne 1) <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="adresse_l1"
                    className={`cabinet-input ${errors.adresse_l1 ? 'error' : ''}`}
                    value={formData.adresse_l1 || ''}
                    onChange={(e) => handleChange('adresse_l1', e.target.value)}
                    placeholder="123 Rue de la Paix"
                  />
                  {errors.adresse_l1 && <span className="error-message">{errors.adresse_l1}</span>}
                </div>

                {/* Adresse ligne 2 */}
                <div className="cabinet-field">
                  <label htmlFor="adresse_l2" className="cabinet-label">
                    Adresse (ligne 2)
                  </label>
                  <input
                    type="text"
                    id="adresse_l2"
                    className={`cabinet-input ${errors.adresse_l2 ? 'error' : ''}`}
                    value={formData.adresse_l2 || ''}
                    onChange={(e) => handleChange('adresse_l2', e.target.value)}
                    placeholder="Complément d'adresse"
                  />
                  {errors.adresse_l2 && <span className="error-message">{errors.adresse_l2}</span>}
                </div>

                {/* Code postal */}
                <div className="cabinet-field">
                  <label htmlFor="code_postal" className="cabinet-label">
                    Code postal <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="code_postal"
                    className={`cabinet-input ${errors.code_postal ? 'error' : ''}`}
                    value={formData.code_postal || ''}
                    onChange={(e) => handleChange('code_postal', e.target.value)}
                    placeholder="75001"
                  />
                  {errors.code_postal && <span className="error-message">{errors.code_postal}</span>}
                </div>

                {/* Ville */}
                <div className="cabinet-field">
                  <label htmlFor="ville" className="cabinet-label">
                    Ville <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="ville"
                    className={`cabinet-input ${errors.ville ? 'error' : ''}`}
                    value={formData.ville || ''}
                    onChange={(e) => handleChange('ville', e.target.value)}
                    placeholder="Paris"
                  />
                  {errors.ville && <span className="error-message">{errors.ville}</span>}
                </div>

                {/* Pays */}
                <div className="cabinet-field">
                  <label htmlFor="pays" className="cabinet-label">
                    Pays <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="pays"
                    className={`cabinet-input ${errors.pays ? 'error' : ''}`}
                    value={formData.pays || ''}
                    onChange={(e) => handleChange('pays', e.target.value)}
                    placeholder="FR"
                  />
                  {errors.pays && <span className="error-message">{errors.pays}</span>}
                </div>
              </div>
            </section>

            {/* Séparateur */}
            <div className="cabinet-separator" />

            {/* Bloc 4 - Organismes */}
            <section className="cabinet-section">
              <h3 className="cabinet-section-title">Organismes</h3>
              <div className="cabinet-grid">
                {/* Prévoyance */}
                <div className="cabinet-field">
                  <label htmlFor="organisme_prevoyance" className="cabinet-label">
                    Prévoyance
                    {isOrganismeEmpty('prevoyance') && (
                      <span className="organisme-alert">
                        {getOrganismeAlertMessage('prevoyance')}
                      </span>
                    )}
                  </label>
                  <select
                    id="organisme_prevoyance"
                    className={`cabinet-input cabinet-select ${errors.organisme_prevoyance ? 'error' : ''}`}
                    value={formData.organisme_prevoyance || ''}
                    onChange={(e) => handleChange('organisme_prevoyance', e.target.value)}
                  >
                    <option value="">Sélectionnez un organisme</option>
                    {getOrganismeOptions('prevoyance').map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.organisme_prevoyance && <span className="error-message">{errors.organisme_prevoyance}</span>}
                </div>

                {/* Mutuelle */}
                <div className="cabinet-field">
                  <label htmlFor="organisme_mutuelle" className="cabinet-label">
                    Mutuelle
                    {isOrganismeEmpty('mutuelle') && (
                      <span className="organisme-alert">
                        {getOrganismeAlertMessage('mutuelle')}
                      </span>
                    )}
                  </label>
                  <select
                    id="organisme_mutuelle"
                    className={`cabinet-input cabinet-select ${errors.organisme_mutuelle ? 'error' : ''}`}
                    value={formData.organisme_mutuelle || ''}
                    onChange={(e) => handleChange('organisme_mutuelle', e.target.value)}
                  >
                    <option value="">Sélectionnez un organisme</option>
                    {getOrganismeOptions('mutuelle').map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.organisme_mutuelle && <span className="error-message">{errors.organisme_mutuelle}</span>}
                </div>

                {/* Retraite complémentaire */}
                <div className="cabinet-field">
                  <label htmlFor="organisme_retraite_comp" className="cabinet-label">
                    Retraite complémentaire (Agirc-Arrco)
                    {isOrganismeEmpty('retraite_comp') && (
                      <span className="organisme-alert">
                        {getOrganismeAlertMessage('retraite_comp')}
                      </span>
                    )}
                  </label>
                  <select
                    id="organisme_retraite_comp"
                    className={`cabinet-input cabinet-select ${errors.organisme_retraite_comp ? 'error' : ''}`}
                    value={formData.organisme_retraite_comp || ''}
                    onChange={(e) => handleChange('organisme_retraite_comp', e.target.value)}
                  >
                    <option value="">Sélectionnez une institution</option>
                    {getOrganismeOptions('retraite_comp').map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.organisme_retraite_comp && <span className="error-message">{errors.organisme_retraite_comp}</span>}
                </div>
              </div>
              
              {/* Aide contextuelle */}
              {formData.convention_idcc === '1619' ? (
                <div className="cabinet-organismes-help">
                  Pour les <strong>cabinets dentaires (IDCC 1619)</strong>, la mutuelle et la prévoyance doivent être souscrites auprès de l'<strong>APGIS</strong>. Vous pouvez néanmoins sélectionner un autre organisme, mais la paie risque d'être non conforme.
                </div>
              ) : (
                <div className="cabinet-organismes-help">
                  Choisissez l'organisme réellement souscrit par votre cabinet. La liste ci-dessus contient les plus fréquents, vous pouvez saisir <em>Autre…</em> si besoin.
                </div>
              )}
            </section>

            {/* Séparateur */}
            <div className="cabinet-separator" />

            {/* Bloc 5 - Affiliations & Complémentaires */}
            <section className="cabinet-section">
              <h3 className="cabinet-section-title">Affiliations & Complémentaires</h3>
              
              {/* A. URSSAF */}
              <div className="cabinet-subsection">
                <h4 className="cabinet-subsection-title">Affiliation URSSAF</h4>
                <div className="cabinet-grid">
                  <div className="cabinet-field">
                    <label htmlFor="urssaf_compte" className="cabinet-label">
                      Compte URSSAF / N° d'affiliation
                    </label>
                    <input
                      type="text"
                      id="urssaf_compte"
                      className={`cabinet-input ${errors.urssaf_compte ? 'error' : ''}`}
                      value={formData.urssaf_compte || ''}
                      onChange={(e) => handleChange('urssaf_compte', e.target.value)}
                      placeholder="ex: 123456789"
                    />
                    <span className="cabinet-hint">Utilisé pour paramétrer vos déclarations sociales (URSSAF)</span>
                    {errors.urssaf_compte && <span className="error-message">{errors.urssaf_compte}</span>}
                  </div>
                </div>
              </div>

              {/* B. Mutuelle */}
              <div className="cabinet-subsection">
                <h4 className="cabinet-subsection-title">Mutuelle (complémentaire santé)</h4>
                <div className="cabinet-grid">
                  <div className="cabinet-field">
                    <label htmlFor="mutuelle_name" className="cabinet-label">
                      Nom de la mutuelle
                    </label>
                    <input
                      type="text"
                      id="mutuelle_name"
                      className={`cabinet-input ${errors.mutuelle_name ? 'error' : ''}`}
                      value={formData.mutuelle_name || ''}
                      onChange={(e) => handleChange('mutuelle_name', e.target.value)}
                      placeholder="ex: APGIS, Harmonie Mutuelle..."
                    />
                    {errors.mutuelle_name && <span className="error-message">{errors.mutuelle_name}</span>}
                  </div>

                  <div className="cabinet-field">
                    <label htmlFor="mutuelle_premium_total" className="cabinet-label">
                      Prime totale mensuelle (€)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      id="mutuelle_premium_total"
                      className={`cabinet-input ${errors.mutuelle_premium_total ? 'error' : ''}`}
                      value={formData.mutuelle_premium_total}
                      onChange={(e) => handleChange('mutuelle_premium_total', e.target.value)}
                      placeholder="ex: 100.00"
                    />
                    <span className="cabinet-hint">Montant total de la cotisation mensuelle</span>
                    {errors.mutuelle_premium_total && <span className="error-message">{errors.mutuelle_premium_total}</span>}
                  </div>

                  <div className="cabinet-field">
                    <label htmlFor="mutuelle_employer_share_percent" className="cabinet-label">
                      Part employeur (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      id="mutuelle_employer_share_percent"
                      className={`cabinet-input ${errors.mutuelle_employer_share_percent ? 'error' : ''}`}
                      value={formData.mutuelle_employer_share_percent}
                      onChange={(e) => handleChange('mutuelle_employer_share_percent', e.target.value)}
                      placeholder="ex: 50"
                    />
                    <span className="cabinet-hint">Pourcentage de la prime pris en charge par l'employeur</span>
                    {errors.mutuelle_employer_share_percent && <span className="error-message">{errors.mutuelle_employer_share_percent}</span>}
                  </div>
                </div>
              </div>

              {/* C. Prévoyance */}
              <div className="cabinet-subsection">
                <h4 className="cabinet-subsection-title">Prévoyance</h4>
                <div className="cabinet-grid">
                  <div className="cabinet-field">
                    <label htmlFor="prevoyance_name" className="cabinet-label">
                      Nom de la prévoyance
                    </label>
                    <input
                      type="text"
                      id="prevoyance_name"
                      className={`cabinet-input ${errors.prevoyance_name ? 'error' : ''}`}
                      value={formData.prevoyance_name || ''}
                      onChange={(e) => handleChange('prevoyance_name', e.target.value)}
                      placeholder="ex: APGIS, AG2R..."
                    />
                    {errors.prevoyance_name && <span className="error-message">{errors.prevoyance_name}</span>}
                  </div>

                  <div className="cabinet-field">
                    <label htmlFor="prevoyance_mode" className="cabinet-label">
                      Mode de calcul
                    </label>
                    <select
                      id="prevoyance_mode"
                      className={`cabinet-input cabinet-select ${errors.prevoyance_mode ? 'error' : ''}`}
                      value={formData.prevoyance_mode || 'FORFAIT'}
                      onChange={(e) => handleChange('prevoyance_mode', e.target.value)}
                    >
                      <option value="FORFAIT">Forfait (montant fixe)</option>
                      <option value="PERCENT">Pourcentage (tranches T1/T2)</option>
                    </select>
                    <span className="cabinet-hint">Choisissez le mode de calcul de la cotisation</span>
                    {errors.prevoyance_mode && <span className="error-message">{errors.prevoyance_mode}</span>}
                  </div>
                </div>

                {/* Mode FORFAIT */}
                {formData.prevoyance_mode === 'FORFAIT' && (
                  <div className="cabinet-grid" style={{ marginTop: '16px' }}>
                    <div className="cabinet-field">
                      <label htmlFor="prevoyance_premium_total" className="cabinet-label">
                        Prime totale mensuelle (€)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="prevoyance_premium_total"
                        className={`cabinet-input ${errors.prevoyance_premium_total ? 'error' : ''}`}
                        value={formData.prevoyance_premium_total}
                        onChange={(e) => handleChange('prevoyance_premium_total', e.target.value)}
                        placeholder="ex: 80.00"
                      />
                      <span className="cabinet-hint">Montant total de la cotisation mensuelle</span>
                      {errors.prevoyance_premium_total && <span className="error-message">{errors.prevoyance_premium_total}</span>}
                    </div>

                    <div className="cabinet-field">
                      <label htmlFor="prevoyance_employer_share_percent" className="cabinet-label">
                        Part employeur (%)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        id="prevoyance_employer_share_percent"
                        className={`cabinet-input ${errors.prevoyance_employer_share_percent ? 'error' : ''}`}
                        value={formData.prevoyance_employer_share_percent}
                        onChange={(e) => handleChange('prevoyance_employer_share_percent', e.target.value)}
                        placeholder="ex: 60"
                      />
                      <span className="cabinet-hint">Pourcentage de la prime pris en charge par l'employeur</span>
                      {errors.prevoyance_employer_share_percent && <span className="error-message">{errors.prevoyance_employer_share_percent}</span>}
                    </div>
                  </div>
                )}

                {/* Mode PERCENT */}
                {formData.prevoyance_mode === 'PERCENT' && (
                  <div className="cabinet-grid" style={{ marginTop: '16px' }}>
                    <div className="cabinet-field">
                      <label htmlFor="prevoyance_t1_rate_employee" className="cabinet-label">
                        T1 - Taux salarié (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="prevoyance_t1_rate_employee"
                        className={`cabinet-input ${errors.prevoyance_t1_rate_employee ? 'error' : ''}`}
                        value={formData.prevoyance_t1_rate_employee}
                        onChange={(e) => handleChange('prevoyance_t1_rate_employee', e.target.value)}
                        placeholder="ex: 0.50"
                      />
                      <span className="cabinet-hint">Tranche 1 (&#8804; 1 PASS)</span>
                      {errors.prevoyance_t1_rate_employee && <span className="error-message">{errors.prevoyance_t1_rate_employee}</span>}
                    </div>

                    <div className="cabinet-field">
                      <label htmlFor="prevoyance_t1_rate_employer" className="cabinet-label">
                        T1 - Taux employeur (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="prevoyance_t1_rate_employer"
                        className={`cabinet-input ${errors.prevoyance_t1_rate_employer ? 'error' : ''}`}
                        value={formData.prevoyance_t1_rate_employer}
                        onChange={(e) => handleChange('prevoyance_t1_rate_employer', e.target.value)}
                        placeholder="ex: 1.00"
                      />
                      <span className="cabinet-hint">Tranche 1 (&#8804; 1 PASS)</span>
                      {errors.prevoyance_t1_rate_employer && <span className="error-message">{errors.prevoyance_t1_rate_employer}</span>}
                    </div>

                    <div className="cabinet-field">
                      <label htmlFor="prevoyance_t2_rate_employee" className="cabinet-label">
                        T2 - Taux salarié (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="prevoyance_t2_rate_employee"
                        className={`cabinet-input ${errors.prevoyance_t2_rate_employee ? 'error' : ''}`}
                        value={formData.prevoyance_t2_rate_employee}
                        onChange={(e) => handleChange('prevoyance_t2_rate_employee', e.target.value)}
                        placeholder="ex: 0.75"
                      />
                      <span className="cabinet-hint">Tranche 2 (&gt; 1 PASS)</span>
                      {errors.prevoyance_t2_rate_employee && <span className="error-message">{errors.prevoyance_t2_rate_employee}</span>}
                    </div>

                    <div className="cabinet-field">
                      <label htmlFor="prevoyance_t2_rate_employer" className="cabinet-label">
                        T2 - Taux employeur (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="prevoyance_t2_rate_employer"
                        className={`cabinet-input ${errors.prevoyance_t2_rate_employer ? 'error' : ''}`}
                        value={formData.prevoyance_t2_rate_employer}
                        onChange={(e) => handleChange('prevoyance_t2_rate_employer', e.target.value)}
                        placeholder="ex: 1.50"
                      />
                      <span className="cabinet-hint">Tranche 2 (&gt; 1 PASS)</span>
                      {errors.prevoyance_t2_rate_employer && <span className="error-message">{errors.prevoyance_t2_rate_employer}</span>}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Séparateur */}
            <div className="cabinet-separator" />

            {/* Bloc 6 - Note */}
            <section className="cabinet-section">
              <h3 className="cabinet-section-title">Note</h3>
              <div className="cabinet-grid">
                <div className="cabinet-field">
                  <label htmlFor="note" className="cabinet-label">
                    Note interne
                  </label>
                  <textarea
                    id="note"
                    className={`cabinet-input ${errors.note ? 'error' : ''}`}
                    value={formData.note || ''}
                    onChange={(e) => handleChange('note', e.target.value)}
                    placeholder="Notes internes sur le cabinet..."
                    rows={3}
                  />
                  {errors.note && <span className="error-message">{errors.note}</span>}
                </div>
              </div>
            </section>

            {/* Séparateur */}
            <div className="cabinet-separator" />

            {/* Bloc 7 - Coordonnées bancaires (toujours en dernier) */}
            <section className="cabinet-section">
              <h3 className="cabinet-section-title">Coordonnées bancaires</h3>
              <div className="cabinet-banking-help">
                Nécessaire si vous souhaitez préparer les virements salariaux / prélèvements URSSAF.
              </div>
              <div className="cabinet-grid">
                {/* IBAN */}
                <div className="cabinet-field">
                  <label htmlFor="iban" className="cabinet-label">
                    IBAN
                  </label>
                  <input
                    type="text"
                    id="iban"
                    className={`cabinet-input ${errors.iban ? 'error' : ''}`}
                    value={formData.iban || ''}
                    onChange={(e) => handleChange('iban', e.target.value)}
                    placeholder="FR76..."
                  />
                  {errors.iban && <span className="error-message">{errors.iban}</span>}
                </div>

                {/* BIC */}
                <div className="cabinet-field">
                  <label htmlFor="bic" className="cabinet-label">
                    BIC
                  </label>
                  <input
                    type="text"
                    id="bic"
                    className={`cabinet-input ${errors.bic ? 'error' : ''}`}
                    value={formData.bic || ''}
                    onChange={(e) => handleChange('bic', e.target.value)}
                    placeholder="ex: AGRIFRPP"
                  />
                  {errors.bic && <span className="error-message">{errors.bic}</span>}
                </div>
              </div>
            </section>

            {/* Footer */}
            <div className="cabinet-modal-footer">
              <button 
                type="button"
                className="cabinet-btn cabinet-btn-secondary"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Annuler
              </button>
              <button 
                type="submit"
                className="cabinet-btn cabinet-btn-primary"
                disabled={loading}
              >
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
